/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from 'react';
import { FiArrowRight, FiUser, FiMapPin, FiPhone, FiCreditCard, FiCalendar, FiClock, FiEdit3, FiTrash2, FiShoppingBag } from 'react-icons/fi';
import { buildImageUrl } from "../theme1/utils/theme1Helpers";
import { formatNumber } from "../../utils/formatNumber";
import { getDeliveryByCity } from "../utils/storeApi";

export default function OrderDetailsPage({ order, theme, onClose, onEdit, onDelete }) {
    const [shipping, setShipping] = useState(0);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    const customerName =
        order?.customerName ||
        JSON.parse(localStorage.getItem("storeCustomer") || "{}")?.name ||
        "-";

    const validDetails = (order?.details || []).map(d => ({
        itemId: d.itemId || d.ItemId,
        variantId: d.variantId || d.VariantId,
        itemName: d.itemName || d.ItemName,
        variantName: d.variantName || d.VariantName,
        colorName: d.colorName || d.ColorName || d.color,
        size: d.size || d.Size,
        quantity: Number(d.quantity || d.Quantity || 0),
        price: Number(d.price || d.Price || 0),
        lineTotal: Number(d.lineTotal || d.LineTotal || 0),
        imageUrl: d.imageUrl || d.ImageUrl
    })).filter(d => d.quantity > 0);

    // حساب المجاميع بدقة
    const subTotal = validDetails.reduce((s, i) => s + (Number(i.lineTotal) || (Number(i.price) * Number(i.quantity)) || 0), 0);
    const discount = Number(order?.discountAmount || 0);
    const finalTotal = Math.max(subTotal - discount + Number(shipping || 0), 0);

    useEffect(() => {
        let alive = true;
        if (!order) return;
        const tenantId = Number(order.tenantID || order.tenantId || 0);
        const cityId = Number(order.cityId || 0);
        if (!tenantId || !cityId) return;
        getDeliveryByCity(tenantId, cityId).then((res) => {
            if (!alive) return;
            setShipping(Number(res?.deliveryCompanyFees || res?.deliveryFees || res?.fee || 0));
        }).catch(() => { if (alive) setShipping(0); });
        return () => { alive = false; };
    }, [order]);

    if (!order) return null;
    const isNewOrder = Number(order.statusId) === 1;

    return (
        <div className="fixed inset-0 z-[9999] w-full h-screen bg-gray-50 flex flex-col overflow-y-auto" dir="rtl">

            {/* Header - متجاوب بالكامل */}
            <div className="w-full bg-white border-b border-gray-100 sticky top-0 z-[10000] shadow-sm">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 md:py-6 flex items-center justify-between">
                    <div className="flex items-center gap-3 md:gap-4 overflow-hidden">
                        <button
                            onClick={onClose}
                            className="w-9 h-9 md:w-12 md:h-12 flex items-center justify-center rounded-xl md:rounded-2xl bg-gray-50 text-gray-600 active:scale-90 transition-transform flex-shrink-0"
                        >
                            <FiArrowRight size={22} />
                        </button>
                        <div className="flex flex-col truncate">
                            <h3 className="text-base md:text-2xl font-black text-gray-800 truncate">تفاصيل الطلب</h3>
                            <p className="text-[10px] md:text-sm font-medium text-gray-400 leading-none mt-1">
                                رقم الطلب: <span className="text-gray-600 font-bold">#{order.orderNo || order.orderId?.slice(0, 6)}</span>
                            </p>
                        </div>
                    </div>

                    {/* أزرار الديسكتوب فقط */}
                    <div className="hidden md:flex gap-3">
                        {isNewOrder && (
                            <button onClick={() => onEdit(order)} className="px-6 py-3 bg-white border-2 border-gray-100 text-gray-700 rounded-2xl font-bold flex items-center gap-2 hover:border-gray-200 transition-all shadow-sm">
                                <FiEdit3 /> تعديل
                            </button>
                        )}
                        {(isNewOrder && Number(order.isPaid) === 0) && (
                            <button onClick={() => onDelete(order)} className="px-6 py-3 bg-red-500 text-white rounded-2xl font-bold flex items-center gap-2 shadow-lg shadow-red-100 transition-all">
                                <FiTrash2 /> حذف
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <main className="flex-1 w-full max-w-7xl mx-auto p-4 md:p-8 space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">

                    {/* الجانب الأيمن: بيانات العميل - تم تحسين العرض للجوال لتكون تحت بعض */}
                    <div className="lg:col-span-4 space-y-4 md:space-y-6">
                        <section className="bg-white rounded-[1.5rem] md:rounded-[2.5rem] p-5 md:p-8 shadow-sm border border-gray-100">
                            <h4 className="text-base md:text-lg font-black text-gray-800 mb-5 md:mb-6 flex items-center gap-2">
                                <div className={`p-1.5 md:p-2 rounded-lg bg-opacity-10 ${theme.bg} ${theme.text}`}>
                                    <FiUser size={18} />
                                </div>
                                بيانات العميل
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-5 md:gap-6">
                                <InfoItem icon={<FiUser />} label="اسم العميل" value={customerName} />
                                <InfoItem icon={<FiPhone />} label="رقم التواصل" value={order.customerPhone1} />
                                <InfoItem icon={<FiMapPin />} label="عنوان التوصيل" value={`${order.city || '-'}، ${order.address || '-'}`} />
                                <InfoItem icon={<FiCalendar />} label="تاريخ الطلب" value={order.orderDate} />
                                <InfoItem icon={<FiCreditCard />} label="طريقة الدفع" value={order.paymentMethodName} />
                                <div className="pt-3 border-t border-gray-50 col-span-full">
                                    <InfoItem icon={<FiClock />} label="حالة الطلب" value={order.status} theme={theme} isBadge />
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* الجانب الأيسر: المنتجات */}
                    <div className="lg:col-span-8 space-y-4 md:space-y-6">
                        <section className="bg-white rounded-[1.5rem] md:rounded-[2.5rem] p-5 md:p-8 shadow-sm border border-gray-100">
                            <div className="flex items-center justify-between mb-6 md:mb-8">
                                <h4 className="text-base md:text-lg font-black text-gray-800 flex items-center gap-2">
                                    <div className={`p-1.5 md:p-2 rounded-lg bg-opacity-10 ${theme.bg} ${theme.text}`}>
                                        <FiShoppingBag size={18} />
                                    </div>
                                    المنتجات
                                </h4>
                                <span className="bg-gray-50 text-gray-500 px-3 py-1 rounded-lg text-xs font-bold border border-gray-100">
                                    {validDetails.length} أصناف
                                </span>
                            </div>

                            <div className="space-y-3">
                                {validDetails.map((item, i) => (
                                    <div key={`full-page-${item.itemId}-${i}`} className="flex gap-3 md:gap-5 p-3 md:p-5 rounded-[1.2rem] md:rounded-[2rem] border border-gray-50 hover:bg-gray-50/30 transition-all">
                                        <div className="w-16 h-16 md:w-28 md:h-28 bg-gray-50 rounded-xl md:rounded-[1.5rem] overflow-hidden flex-shrink-0 border border-gray-100">
                                            <img
                                                src={buildImageUrl(item.imageUrl)}
                                                onError={e => e.target.src = "https://placehold.co/100x100?text=No+Image"}
                                                className="w-full h-full object-contain p-1 md:p-2"
                                                alt=""
                                            />
                                        </div>

                                        <div className="flex-1 min-w-0 flex flex-col justify-between">
                                            <div className="flex flex-col sm:flex-row justify-between items-start gap-1">
                                                <h5 className="font-bold text-gray-800 text-xs md:text-lg truncate w-full">{item.itemName}</h5>
                                                <span className={`font-black text-sm md:text-xl ${theme.text} whitespace-nowrap`}>
                                                    {formatNumber(item.lineTotal || item.price * item.quantity)} <span className="text-[9px] md:text-xs uppercase">د.ع</span>
                                                </span>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                                                {item.variantName && <span className="text-[9px] md:text-[11px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md">النوع: {item.variantName}</span>}
                                                {item.size && <span className="text-[9px] md:text-[11px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md">المقاس: {item.size}</span>}
                                                <span className={`text-[9px] md:text-[11px] font-black px-2 py-0.5 rounded-md bg-opacity-10 ${theme.bg} ${theme.text}`}>العدد: {item.quantity}</span>
                                            </div>
                                            <p className="text-[9px] md:text-[11px] text-gray-400 mt-1 md:mt-2">سعر الوحدة: {formatNumber(item.price)} د.ع</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* ملخص الحساب السفلي - مُحسن للموبايل */}
                            <div className="mt-8 pt-6 border-t border-gray-100">
                                <div className="w-full md:max-w-md md:mr-auto space-y-3">
                                    <div className="flex justify-between items-center text-gray-400 font-bold px-4 text-xs md:text-sm">
                                        <span>إجمالي المنتجات</span>
                                        <span className="text-gray-700">{formatNumber(subTotal)} د.ع</span>
                                    </div>
                                    {discount > 0 && (
                                        <div className="flex justify-between items-center text-red-500 font-bold px-4 text-xs md:text-sm">
                                            <span>الخصم</span>
                                            <span>-{formatNumber(discount)} د.ع</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center text-gray-400 font-bold px-4 text-xs md:text-sm">
                                        <span>أجور التوصيل</span>
                                        <span className="text-gray-700">
                                            {Number(shipping) <= 0 ? (
                                                <span className="text-green-500">مجاني ✨</span>
                                            ) : (
                                                `${formatNumber(shipping)} د.ع`
                                            )}
                                        </span>
                                    </div>
                                    <div className={`flex justify-between items-center p-4 md:p-6 rounded-[1.2rem] md:rounded-[2rem] bg-opacity-10 ${theme.bg}`}>
                                        <span className="text-gray-800 font-black text-sm md:text-lg uppercase tracking-widest">المجموع الكلي</span>
                                        <span className={`text-lg md:text-2xl font-black ${theme.text}`}>{formatNumber(finalTotal)} د.ع</span>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>

                {/* أزرار التحكم في الموبايل - مرتبة بشكل أفضل */}
                <div className="md:hidden grid grid-cols-1 gap-3 pb-12">
                    {isNewOrder && (
                        <button
                            onClick={() => onEdit(order)}
                            className="w-full bg-white border-2 border-gray-100 text-gray-700 h-14 rounded-xl font-black flex items-center justify-center gap-2"
                        >
                            <FiEdit3 size={18} /> تعديل الطلب
                        </button>
                    )}
                    {(isNewOrder && Number(order.isPaid) === 0) && (
                        <button
                            onClick={() => onDelete(order)}
                            className="w-full bg-red-500 text-white h-14 rounded-xl font-black flex items-center justify-center gap-2 shadow-lg shadow-red-200"
                        >
                            <FiTrash2 size={18} /> حذف الطلب
                        </button>
                    )}
                </div>
            </main>
        </div>
    );
}

const InfoItem = ({ icon, label, value, theme, isBadge }) => (
    <div className="flex items-start gap-3 md:gap-4 group min-w-0">
        <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-gray-50 border border-gray-50 flex items-center justify-center text-gray-400 flex-shrink-0 group-hover:bg-white group-hover:shadow-md transition-all">
            {React.cloneElement(icon, { className: "w-4 h-4 md:w-[18px] md:h-[18px]" })}
        </div>
        <div className="flex flex-col min-w-0 flex-1">
            <span className="text-[9px] md:text-[10px] text-gray-400 font-bold mb-0.5 uppercase tracking-tight md:tracking-widest">{label}</span>
            <span className={`text-xs md:text-base font-bold break-words leading-tight ${isBadge ? `inline-block px-2 py-0.5 md:px-3 md:py-1 rounded-lg bg-opacity-10 ${theme.bg} ${theme.text} border border-current w-fit` : 'text-gray-700'}`}>
                {value || "-"}
            </span>
        </div>
    </div>
);
